import { useCallback } from 'react'
import { useNonce } from '#app/utils/nonce-provider.ts'

type TurnstileApi = {
	render: (el: HTMLElement, options: Record<string, unknown>) => string
	remove: (widgetId: string) => void
	reset: (widgetId: string) => void
}

declare global {
	interface Window {
		turnstile?: TurnstileApi
	}
}

// `render=explicit` disables Turnstile's implicit rendering, which scans the
// document for `.cf-turnstile` on script load. Implicit mode cannot work here:
// on a hard load it injects the iframe into a div React is about to hydrate, and
// hydration deletes children React didn't render; on a client-side navigation
// the script has long since finished scanning, so a form that mounts later never
// gets a widget at all. Either way the form posts with no
// `cf-turnstile-response` and the server rejects it as a failed bot check.
const SCRIPT_SRC =
	'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

let scriptPromise: Promise<void> | null = null

/** Load api.js once per document, no matter how many widgets are on the page. */
function loadTurnstileScript(nonce: string): Promise<void> {
	scriptPromise ??= new Promise<void>((resolve, reject) => {
		const script = document.createElement('script')
		script.src = SCRIPT_SRC
		// `defer` per the docs' explicit-rendering snippet. Do NOT pair this with
		// `turnstile.ready()`: that call throws "Remove async/defer from the
		// Turnstile api.js script tag before using turnstile.ready()". With defer
		// the API is fully initialised by the time the load event fires, so
		// render() can be called directly and ready() is redundant.
		script.defer = true
		// Satisfies the strict-dynamic CSP. Even without a nonce this is allowed,
		// since strict-dynamic propagates trust from the script that injects it.
		if (nonce) script.nonce = nonce
		script.addEventListener('load', () => resolve())
		script.addEventListener('error', () => {
			scriptPromise = null // let a later widget retry the load
			reject(new Error('Turnstile script failed to load'))
		})
		document.head.append(script)
	})
	return scriptPromise
}

/**
 * Cloudflare Turnstile widget. Renders the challenge inside the enclosing
 * <form> and, on success, injects a hidden `cf-turnstile-response` input that
 * the server verifies (see abuse.server.ts `verifyTurnstile`).
 *
 * Only render this when a site key is configured. When it's absent (local dev
 * without keys) the server-side verify fails open, so the checker still works.
 */
export function TurnstileWidget({ siteKey }: { siteKey: string }) {
	const nonce = useNonce()

	// A ref callback rather than an effect: this is an imperative third-party
	// widget that has to be created and destroyed alongside a specific DOM node.
	const mount = useCallback(
		(node: HTMLDivElement | null) => {
			if (!node) return
			let widgetId: string | null = null
			let cancelled = false

			// `appearance: interaction-only` stops Turnstile drawing a box, but the
			// container is still a flex child, so an empty one would contribute the
			// form's `gap` as dead white space. Collapsing it out of the layout
			// entirely is the only way the form looks untouched; the hidden
			// `cf-turnstile-response` input still submits from a display:none
			// subtree. Turnstile tells us when a real challenge is coming, which is
			// the moment to give it room. `replace` rather than add/remove because
			// `hidden` and `flex` are both display utilities, so which one wins is
			// decided by their order in the stylesheet, not in the class list.
			const show = () => node.classList.replace('hidden', 'flex')
			const hide = () => node.classList.replace('flex', 'hidden')

			// A *spent* token is the one failure mode Turnstile will not heal on its
			// own: tokens are single-use, and a form that stays mounted across a
			// submission (an inline error, or the fetcher-driven report form) would
			// replay one and be rejected as `timeout-or-duplicate`. Expiry, timeout
			// and network errors need no handling here — `refresh-expired`,
			// `refresh-timeout` and `retry` all default to `auto`, and resetting
			// underneath those would interrupt the refresh the widget is doing.
			const form = node.closest('form')
			const onSubmit = () => {
				// Deferred past the submit event: by the next macrotask the FormData
				// carrying the current token has already been serialized and sent.
				setTimeout(() => {
					if (!widgetId) return
					window.turnstile?.reset(widgetId)
					// The fresh challenge starts non-interactive again, and nothing
					// fires after-interactive-callback for a widget that was reset
					// while visible.
					hide()
				}, 0)
			}
			form?.addEventListener('submit', onSubmit)

			void loadTurnstileScript(nonce)
				.then(() => {
					if (cancelled || !window.turnstile) return
					widgetId = window.turnstile.render(node, {
						sitekey: siteKey,
						// The marketing pages sit on a static light canvas that does not
						// follow the OS theme, so `auto` would render a dark widget on a
						// white card.
						theme: 'light',
						// "The widget becomes visible only when visitor interaction is
						// required." The challenge still runs for everyone and still
						// produces a token; almost nobody ever sees a box. This is what
						// keeps a 65px slab of grey out of the middle of the hero form.
						appearance: 'interaction-only',
						'before-interactive-callback': show,
						'after-interactive-callback': hide,
						'error-callback': (code: string) => {
							// Logged, not handled: returning a truthy value here would
							// suppress Turnstile's own retry and error UI.
							console.error('[turnstile] challenge error', code)
						},
					})
				})
				.catch((error: unknown) => {
					console.error('[turnstile] widget failed to render', error)
				})

			return () => {
				cancelled = true
				form?.removeEventListener('submit', onSubmit)
				if (widgetId) window.turnstile?.remove(widgetId)
			}
		},
		[siteKey, nonce],
	)

	// Starts collapsed and owns its own spacing, so callers can drop it straight
	// into a form without a wrapper that would leave a gap behind when hidden.
	return <div ref={mount} className="hidden justify-center pt-1" />
}
